import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';

import { toastError, toastSuccess } from '@components/Toast';

import {
	useSaveQtyWisePriceMutation,
	useSaveOcsOfferInfoMutation,
	useGetOcsOfferInfoMutation
} from '@features/costing-agent/api/costingAgentApi';

import LoaderComponent from '@/components/LoaderComponent';
import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

import { getQuantityWisePrice, COSTING_MIX_PANEL_EVENTS } from './../utils';

const CostingHeader = ({
	historyId,
	totalFobPerUnit,
	baseQty,
	quantityWisePrices = [],
	onHistoryRefetch,
	onCostingRefetch,
	initialCostingId,
	productId,
	historyDetailId,
	requestBodyTechpackCostingId,
	scenarioDataList,
	selectedScenarioId,
	isShowForScenario = false,
	refreshKey,
	onOcsOfferLoadingChange,
	onOptimizeOffer,
	costingBrandInfo,
	isFromOptimizeAndOffer = false
}) => {
	const [productionPrice, setProductionPrice] = useState('');
	const [orderQuantity, setOrderQuantity] = useState(costingBrandInfo?.singleQty);
	const [salesMargin, setSalesMargin] = useState('');
	const [salesMarginError, setSalesMarginError] = useState('');
	const [ocsOfferInfo, setOcsOfferInfo] = useState(null);
	const lastSavedOrderQuantityRef = useRef(orderQuantity);
	const [saveQtyWisePrice, { isLoading: isSaveQtyWisePriceLoading }] = useSaveQtyWisePriceMutation();
	const [saveOcsOfferInfo, { isLoading: isSaveOcsOfferInfoLoading }] = useSaveOcsOfferInfoMutation();
	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const storedFobDecisionData = useSelector((state) => state.costingAgent.fobDecisionResponse);
	const targetFobPrice = Number(storedFobDecisionData?.targetedFobPrice);
	// stored fob decision data
	const retailReferencePrice = Number(storedFobDecisionData?.retailReferencePrice);
	const exportCostId = storedFobDecisionData?.exportCostId;
	const dutyPct = Number(storedFobDecisionData?.dutyPercentage);
	const shippingPct = Number(storedFobDecisionData?.shippingPercentage);
	const warehousePct = Number(storedFobDecisionData?.warehousePercentage);
	const otherCostPct = Number(storedFobDecisionData?.otherCostPercentage);
	const vatPct = Number(storedFobDecisionData?.vatPercentage);
	const retailMarginPct = Number(storedFobDecisionData?.retailPercentage);
	const shippingValue = Number(storedFobDecisionData?.shippingValue);
	const shippingType = storedFobDecisionData?.shippingType;
	const usdRate = Number(
		storedFobDecisionData?.usdConversionRate ?? storedFobDecisionData?.usdConReference ?? 1
	);

	const [getOcsOfferInfo, { isLoading: isGetOcsOfferInfoLoading }] = useGetOcsOfferInfoMutation();
	const selectedOcsOfferInfo =
		ocsOfferInfo?.find((scenario) => scenario.name === selectedScenarioId) || ocsOfferInfo?.[0];

	///qty wise price
	const selectedQtyWisePrice = quantityWisePrices.find(
		(item) => parseInt(item.qty) === parseInt(orderQuantity)
	)?.price;

	const ocsFobPrice = selectedQtyWisePrice || '';

	const currentProductionCost = Number(
		productionPrice || ocsFobPrice || selectedOcsOfferInfo?.productionCost
	);
	const currentSalesMargin = Number(salesMargin || selectedOcsOfferInfo?.salesMarginPercentage);
	// calculate offered fob
	const calculatedOfferedFob =
		currentProductionCost > 0
			? currentProductionCost + (currentProductionCost * currentSalesMargin) / 100
			: selectedOcsOfferInfo?.offeredFobPrice != null
				? Number(selectedOcsOfferInfo.offeredFobPrice)
				: null;

	// calculate projected ref
	const calculateProjectedRef = () => {
		if (calculatedOfferedFob == null || calculatedOfferedFob <= 0) {
			return selectedOcsOfferInfo?.projectedRef != null
				? Number(selectedOcsOfferInfo.projectedRef).toFixed(2)
				: null;
		}

		const totalBuyerCosts = (shippingPct + warehousePct + dutyPct + otherCostPct) / 100;
		const marginDecimal = retailMarginPct > 1 ? retailMarginPct / 100 : retailMarginPct;
		const marginFactor = 1 - marginDecimal;

		if (marginFactor <= 0 || usdRate <= 0) {
			return selectedOcsOfferInfo?.projectedRef != null
				? Number(selectedOcsOfferInfo.projectedRef).toFixed(2)
				: null;
		}

		const landed = calculatedOfferedFob * (1 + totalBuyerCosts);
		const netRetailUsd = landed / marginFactor;
		const netValue = netRetailUsd / usdRate;
		const projected = netValue * (1 + vatPct / 100);

		return projected.toFixed(2);
	};

	const calculateProjectedRefWithValue = () => {
		const marginFactor = (1 - retailMarginPct / 100) * usdRate;

		if (calculatedOfferedFob == null || calculatedOfferedFob <= 0 || marginFactor <= 0) {
			return selectedOcsOfferInfo?.projectedRef != null
				? Number(selectedOcsOfferInfo.projectedRef).toFixed(2)
				: null;
		}

		const buyerCostFactor = 1 + (dutyPct + warehousePct + otherCostPct) / 100;
		const projected =
			(((calculatedOfferedFob + shippingValue) * buyerCostFactor) / marginFactor) * (1 + vatPct / 100);

		return projected.toFixed(2);
	};

	const projectedRefValue =
		shippingType === 'VALUE' ? calculateProjectedRefWithValue() : calculateProjectedRef();
	const currentRetailRef = selectedOcsOfferInfo?.retailRef ?? retailReferencePrice;
	//status calculation
	const computedStatus =
		currentRetailRef != null && projectedRefValue != null
			? currentRetailRef > projectedRefValue
				? 'WITHIN_TARGET'
				: 'BEYOND_TARGET'
			: selectedOcsOfferInfo?.status;

	const fobAmountAboveTarget =
		targetFobPrice > 0 && calculatedOfferedFob != null ? calculatedOfferedFob - targetFobPrice : 0;

	useEffect(() => {
		onOcsOfferLoadingChange?.(
			isSaveQtyWisePriceLoading || isSaveOcsOfferInfoLoading || isGetOcsOfferInfoLoading
		);
	}, [
		isGetOcsOfferInfoLoading,
		isSaveOcsOfferInfoLoading,
		isSaveQtyWisePriceLoading,
		onOcsOfferLoadingChange
	]);

	useEffect(() => {
		const fetchOcsOfferInfo = async () => {
			if (!productId) {
				return;
			}

			try {
				const response = await getOcsOfferInfo({
					productId,
					initialCostingId,
					requestBody: {
						exportCostId: exportCostId
					}
				}).unwrap();
				const offerInfo = response?.payload ?? response;
				setOcsOfferInfo(offerInfo);
				scenarioDataList?.(offerInfo);
			} catch (error) {
				toastError(error?.data?.message || error?.message || 'Failed to fetch OCS offer info.');
			}
		};

		fetchOcsOfferInfo();
	}, [exportCostId, getOcsOfferInfo, historyDetailId, productId, refreshKey, scenarioDataList]);

	useEffect(() => {
		setProductionPrice(selectedOcsOfferInfo?.productionCost?.toString() ?? '');
		setOrderQuantity(
			selectedOcsOfferInfo?.quantity?.toString() ??
				(costingBrandInfo?.singleQty != null ? String(costingBrandInfo.singleQty) : '')
		);
		setSalesMargin(selectedOcsOfferInfo?.salesMarginPercentage?.toString() ?? '');
		setSalesMarginError('');
		lastSavedOrderQuantityRef.current =
			selectedOcsOfferInfo?.quantity?.toString() ??
			(costingBrandInfo?.singleQty != null ? String(costingBrandInfo.singleQty) : '');
	}, [selectedOcsOfferInfo, costingBrandInfo?.singleQty]);

	const handleSaveOcsOfferInfo = async (event) => {
		if (event?.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
			return;
		}

		const currentProductionPrice = productionPrice;
		const currentOrderQuantity = orderQuantity;
		const currentSalesMargin = salesMargin;
		const productionCostForSave = currentProductionPrice || ocsFobPrice;

		if (!currentSalesMargin) {
			setSalesMarginError('Sales margin is a required.');
			return;
		}

		if (!productId || !productionCostForSave || !currentOrderQuantity) {
			return;
		}
		try {
			await saveOcsOfferInfo({
				productId,
				initialCostingId,
				requestBody: {
					techpackCostingId: requestBodyTechpackCostingId
						? requestBodyTechpackCostingId
						: historyDetailId,
					...(selectedOcsOfferInfo?.ocsOfferId
						? { ocsOfferId: selectedOcsOfferInfo.ocsOfferId }
						: {}),
					exportCostId: exportCostId,
					retailRef: retailReferencePrice,
					salesMarginPercentage: Number(currentSalesMargin),
					targetFobPrice: targetFobPrice,
					productionCost: Number(productionCostForSave),
					quantity: Number(currentOrderQuantity),
					offeredFobPrice:
						calculatedOfferedFob != null ? Number(Number(calculatedOfferedFob).toFixed(2)) : null,
					projectedRef: projectedRefValue != null ? Number(projectedRefValue) : null,
					status: computedStatus
				}
			}).unwrap();
			const ocsOfferInfoRes = await getOcsOfferInfo({
				productId,
				initialCostingId,
				requestBody: {
					exportCostId: exportCostId
				}
			}).unwrap();
			const offerInfo = ocsOfferInfoRes?.payload ?? ocsOfferInfoRes;
			setOcsOfferInfo(offerInfo);
			scenarioDataList?.(offerInfo);
			toastSuccess('OCS offer info saved successfully');
		} catch (error) {
			toastError(error?.data?.message || error?.message || 'Failed to save OCS offer info.');
		}
	};

	const handleUseOcsFob = () => {
		if (ocsFobPrice) {
			setProductionPrice(Number(ocsFobPrice).toFixed(2));
		}
	};

	const handleOrderQuantityChange = (value) => {
		setOrderQuantity(value);
		if (!value) {
			setProductionPrice('');
			return;
		}

		const savedPrice = quantityWisePrices.find((item) => parseInt(item.qty) === parseInt(value))?.price;
		const calculatedPrice = savedPrice || getQuantityWisePrice(parseInt(value), totalFobPerUnit, baseQty);
		setProductionPrice(calculatedPrice ? Number(calculatedPrice).toFixed(2) : '');
	};

	// order quantity blur
	const handleOrderQuantityBlur = async () => {
		if (!orderQuantity || parseInt(orderQuantity) === parseInt(lastSavedOrderQuantityRef.current)) {
			return;
		}

		mixPanelTrakingEvent({
			type: COSTING_MIX_PANEL_EVENTS.ORDER_QUANTITY_CHANGE_CLICK,
			body: {
				quantity: parseInt(orderQuantity)
			}
		});

		try {
			const parsedQuantity = parseInt(orderQuantity);
			const quantityWisePrice = getQuantityWisePrice(parsedQuantity, totalFobPerUnit, baseQty);

			if (!Number(quantityWisePrice)) {
				return;
			}

			const updatedQuantityWisePrices = [
				...quantityWisePrices
					.filter((item) => parseInt(item.qty) !== parsedQuantity)
					.map((item) => ({
						qty: parseInt(item.qty),
						price: parseFloat(item.price),
						id: item.id || undefined
					})),
				{
					qty: parsedQuantity,
					price: parseFloat(Number(quantityWisePrice).toFixed(2))
				}
			];

			await saveQtyWisePrice({
				historyId,
				requestBody: updatedQuantityWisePrices
			}).unwrap();
			lastSavedOrderQuantityRef.current = orderQuantity;
			await Promise.all([onHistoryRefetch?.(), onCostingRefetch?.()]);
			toastSuccess('Quantity wise prices saved successfully');
		} catch (error) {
			toastError(error?.data?.message || error?.message || 'Failed to save quantity wise price.');
		}
	};

	return (
		<>
			<LoaderComponent
				loading={isSaveQtyWisePriceLoading || isSaveOcsOfferInfoLoading || isGetOcsOfferInfoLoading}
			/>
			<div className="costing-header-bar mb-3 text-white">
				<div className="costing-header-metric">
					<p className="costing-header-label text-uppercase weight-500">Target FOB</p>
					<p className="costing-header-value regular-14 weight-600">${targetFobPrice.toFixed(2)}</p>
				</div>
				<div className="input-metric d-flex align-items-center gap-3" onBlur={handleSaveOcsOfferInfo}>
					<div className="costing-header-metric costing-header-input-metric">
						<label
							className="costing-header-label text-uppercase weight-500"
							htmlFor="header-production-cost"
						>
							Production Cost
						</label>
						<div className="costing-header-input-wrap costing-header-currency-input position-relative d-flex align-items-center">
							<span className="position-absolute right-2 top-2 text-white regular-12">$</span>
							<input
								id="header-production-cost"
								className="text-white"
								type="text"
								value={productionPrice || ocsFobPrice}
								onChange={(e) => setProductionPrice(e.target.value)}
							/>
						</div>
						<button
							className="costing-header-sub-action weight-600"
							type="button"
							onClick={handleUseOcsFob}
						>
							Use OCS FOB {ocsFobPrice ? `$${Number(ocsFobPrice).toFixed(2)}` : '--'}
						</button>
					</div>

					<div className="costing-header-metric costing-header-input-metric mt-2 d-flex flex-column align-items-start ">
						<label
							className="costing-header-label text-uppercase weight-500"
							htmlFor="header-order-quantity"
						>
							Order Qty
						</label>
						<input
							id="header-order-quantity"
							className="costing-header-input text-white regular-14"
							type="number"
							value={orderQuantity}
							onChange={(e) => handleOrderQuantityChange(e.target.value)}
							onBlur={handleOrderQuantityBlur}
						/>
						<button className="costing-header-sub-action hidden" type="button">
							.
						</button>
					</div>
					<div className="costing-header-metric costing-header-input-metric">
						<label
							className="costing-header-label text-uppercase weight-500"
							htmlFor="header-sales-margin"
						>
							EA / Sales Margin
						</label>
						<div className="costing-header-input-wrap position-relative d-flex align-items-center">
							<input
								id="header-sales-margin"
								className="costing-header-input text-white regular-14 "
								type="text"
								value={salesMargin}
								onChange={(e) => {
									setSalesMargin(e.target.value);
									if (e.target.value) {
										setSalesMarginError('');
									}
								}}
							/>
							<span className="position-absolute right-2 top-2 gray_dark_02 regular-12">%</span>
						</div>
						{/* text-message */}
						{salesMarginError ? (
							<span className="costing-header-sub-action text-danger">{salesMarginError}</span>
						) : (
							<button className="costing-header-sub-action hidden" type="button">
								.
							</button>
						)}
					</div>
				</div>
				<div className="costing-header-metric costing-header-offered-metric">
					<p className="costing-header-label text-uppercase weight-500">Offered FOB</p>
					<p className="costing-header-value regular-14 weight-600">
						{calculatedOfferedFob != null &&
						!Number.isNaN(Number(calculatedOfferedFob)) &&
						Number.isFinite(Number(calculatedOfferedFob))
							? `$${Number(calculatedOfferedFob).toFixed(2)}`
							: '--'}
					</p>
				</div>

				<div className="costing-header-metric costing-header-projection">
					<p className="costing-header-label text-uppercase weight-500">Retail Ref → Projected</p>
					<p className="costing-header-projection-value regular-14 weight-600">
						{Number.isFinite(Number(currentRetailRef))
							? `$${Number(currentRetailRef).toFixed(2)}`
							: '--'}{' '}
						→{' '}
						{projectedRefValue != null &&
						!Number.isNaN(Number(projectedRefValue)) &&
						Number.isFinite(Number(projectedRefValue))
							? `$${projectedRefValue}`
							: '--'}
					</p>
				</div>
				{(computedStatus || selectedOcsOfferInfo?.status) && (
					<span className="costing-header-status weight-600">
						{(computedStatus || selectedOcsOfferInfo?.status)
							?.toLowerCase()
							.replace(/_/g, ' ')
							.replace(/\b\w/g, (char) => char.toUpperCase())}
					</span>
				)}
			</div>
			{/* Over Price Alert */}
			{!isShowForScenario && fobAmountAboveTarget > 0 && (
				<div className="over-price-alert d-flex align-items-center justify-content-between">
					<div className="over-price-alert-content">
						<strong className="regular-12">
							Offered FOB is ${fobAmountAboveTarget.toFixed(2)} above the buyer target.
						</strong>
						<p className="regular-12">
							As stated, the buyer is unlikely to place the order — and dropping the price would
							erode your margin. Open the studio to build costed variations that close the gap.
						</p>
					</div>
					{!isFromOptimizeAndOffer && (
						<button
							className="button size36"
							type="button"
							onClick={() => {
								mixPanelTrakingEvent({
									type: COSTING_MIX_PANEL_EVENTS.OPTIMIZE_OFFER_LICK,
									body: {
										price: fobAmountAboveTarget
									}
								});
								onOptimizeOffer?.();
							}}
						>
							Optimize &amp; Offer
						</button>
					)}
				</div>
			)}
			<LoaderComponent />
		</>
	);
};

export default CostingHeader;
